'use client';
import { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMemberApi } from '../../hooks/useMemberApi';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const navigate = useNavigate();
  const { confirmEmail } = useMemberApi();
  const backgroundImage = '/images/NS_Pattern%201_Navy_2025.webp';

  const verifyEmail = async () => {
    setVerifying(true);
    try {
      if (!token || !email) {
        throw new Error('Invalid verification link');
      }

      const response = await confirmEmail({ 
        email: decodeURIComponent(email), 
        token: decodeURIComponent(token) 
      });
      
      if (response && response.success) {
        setSuccess(true);
        setErrorMessage('');
      } else {
        setSuccess(false);
        setErrorMessage(response?.message || 'Email verification failed');
      }
    } catch (error: any) {
      setSuccess(false);
      setErrorMessage(
        error?.response?.data?.message || 
        error?.message ||
        'An error occurred while verifying your email. Please try again or contact support.'
      );
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (token && email) {
      verifyEmail();
    } else {
      setVerifying(false);
      setSuccess(false);
      setErrorMessage('Invalid verification link. Please check your email and try again.');
    }
  }, [token, email]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => {
        navigate('/login');
      }, 3500);
      return () => clearTimeout(t);
    }
  }, [success, navigate]);

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
      <div
        className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg text-center z-10">
        <img
          src="/images/logo-linnear-navy.png"
          alt="Natshoot Logo"
          className="mx-auto mb-6 w-56"
        />

        <div className="mb-4">
          {verifying ? (
            <>
              <div className="flex justify-center mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
              </div>
              <h1 className="text-xl font-semibold">Verifying your email...</h1>
              <p className="text-gray-600 mt-2">Please wait while we confirm your email address.</p>
            </>
          ) : success ? (
            <>
              <div className="flex justify-center mb-4">
                <svg
                  className="w-16 h-16 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-green-600">Email verified successfully!</h1>
              <p className="text-gray-600 mt-2">
                Your email has been confirmed. You'll be redirected to the login page shortly.
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                <svg
                  className="w-16 h-16 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-red-600">Verification failed</h1>
              <p className="text-gray-600 mt-2">{errorMessage}</p>
            </>
          )}
        </div>

        {!verifying && (
          <div className="flex justify-center gap-3">
            {success ? (
              <Button onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            ) : (
              <>
                <Button onClick={() => navigate('/login')}>
                  Back to Login
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => window.location.href = 'mailto:support@natshoot.co.za'}
                >
                  Contact Support
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}